import type { FC } from 'react';
import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { InvestAddressRegistrationField } from '@alfa-bank/site-invest-address-registration-field';
import { FormDesktop as Form } from '@alfa-bank/site-form/desktop';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { BlockDesktop as Block } from '@alfa-bank/site-block/desktop';
import { ButtonDesktop as Button } from '@alfa-bank/site-button-v2/desktop';
import { FormInput } from '@alfa-bank/site-form-input';
import type { TFormValues } from '@alfa-bank/site-form/src/models';
import type { FormApi } from 'final-form';
import { FormProgressBarV2Desktop } from '@alfa-bank/site-form-progress-bar-v2/desktop';

import type {
    TDispatch,
    IDeliveryBookCourierDeliveryFormValues,
    THandleSelectAddress,
} from '../../store';
import {
    selectShowProgressBar,
    selectDeliveryBookCourierDelivery,
    submitDeliveryBookCourierDelivery,
    setFormFieldsDeliveryBookCourierDelivery,
    selectDeliveryAddressMetaState,
    setFormFieldsDeliveryAddressMeta,
    submitDeliveryAddressMeta,
} from '../../store';
import type { TFormSubmitCallback } from '../../models/common-types';
import {
    COMMENT,
    DELIVERY_ADDRESS,
    DELIVERY_ADDRESS_DADATA,
    DELIVERY_DATE,
    DELIVERY_DATE_SLOT,
    DELIVERY_DATE_SLOT_TIME,
} from '../../config';
import { AnalyticsSpy } from '../../components/analytics-spy';
import { AddressValidationModalsDesktop } from '../../components/address-validation-modals/desktop';
import { AddressSpinner } from '../../components/address-spinner';
import { sendEventSmev } from '../../helpers/proxy-send-event';

import {
    DEFAULT_COMMENT_SETTINGS,
    DEFAULT_DELIVERY_ADDRESS_SETTINGS,
    defaultDeliveryBookCourierDeliverySettingsDesktop,
} from './config/default-values';
import type { TDeliveryBookCourierDeliveryStepDesktop } from './models';
import { DeliverySlotsDesktop } from './components/delivery-slots/desktop';
import { analyticsBookCourierDeliverySmev } from './analytics';

export const DeliveryBookCourierDeliveryDesktop: FC<TDeliveryBookCourierDeliveryStepDesktop> = ({
    deliveryFlow,
    deliveryAddressLabel = defaultDeliveryBookCourierDeliverySettingsDesktop.deliveryAddressLabel,
    deliverySlotsLabel = defaultDeliveryBookCourierDeliverySettingsDesktop.deliverySlotsLabel,
    primaryButtonText = defaultDeliveryBookCourierDeliverySettingsDesktop.primaryButtonText,
    subtitle = defaultDeliveryBookCourierDeliverySettingsDesktop.subtitle,
    progressBarSettings = defaultDeliveryBookCourierDeliverySettingsDesktop.progressBarSettings,
    title = defaultDeliveryBookCourierDeliverySettingsDesktop.title[deliveryFlow],
}) => {
    const showProgressBar = useSelector(selectShowProgressBar);
    const deliveryAddressMetaState = useSelector(selectDeliveryAddressMetaState);
    const deliveryBookCourierDeliveryState = useSelector(selectDeliveryBookCourierDelivery);
    const isLoading =
        deliveryAddressMetaState.isLoading || deliveryBookCourierDeliveryState.isLoading;
    const dispatch = useDispatch<TDispatch>();
    const analytics = analyticsBookCourierDeliverySmev[deliveryFlow];

    const handleOnSubmit = useCallback(
        async (
            _: TFormValues,
            formApi: FormApi<TFormValues>,
            finishSubmitCallback: TFormSubmitCallback
        ) => {
            const fields = formApi.getState()
                .values as unknown as IDeliveryBookCourierDeliveryFormValues;

            dispatch(setFormFieldsDeliveryBookCourierDelivery(fields));
            const errors = await dispatch(
                submitDeliveryBookCourierDelivery({
                    pending: analytics.formBookCourierDeliveryRequestPending,
                    success: analytics.formBookCourierDeliveryRequestSuccess,
                    fail: analytics.formBookCourierDeliveryRequestFail,
                    failTimeSlotIsBooked:
                        analytics.formBookCourierDeliveryRequestFailTimeSlotIsBooked,
                })
            );

            finishSubmitCallback(errors);
        },
        [
            analytics.formBookCourierDeliveryRequestFail,
            analytics.formBookCourierDeliveryRequestFailTimeSlotIsBooked,
            analytics.formBookCourierDeliveryRequestPending,
            analytics.formBookCourierDeliveryRequestSuccess,
            dispatch,
        ]
    );

    const handleOnSelect: THandleSelectAddress = (addressValue, data, isValid) => {
        if (isValid) {
            sendEventSmev(analytics.selectValidAddressClick);
            dispatch(setFormFieldsDeliveryAddressMeta({ dadata: data, addressValue }));
            dispatch(
                submitDeliveryAddressMeta({
                    pending: analytics.formChooseDeliveryAddressRequestPending,
                    success: analytics.formChooseDeliveryAddressRequestSuccess,
                    fail: analytics.formChooseDeliveryAddressRequestFail,
                    failAddressOutOfRange:
                        analytics.formChooseDeliveryAddressRequestFailAddressOutOfRange,
                    failAddressNotFound:
                        analytics.formChooseDeliveryAddressRequestFailAddressNotFound,
                })
            );
        } else {
            sendEventSmev(analytics.selectNotValidAddressClick);
        }
    };

    return (
        <Form
            analytics={{
                submit: analytics.formSubmitSuccess,
                submitOnFail: analytics.formSubmitFail,
            }}
            initialValues={{
                [DELIVERY_ADDRESS]: deliveryAddressMetaState.formState[DELIVERY_ADDRESS],
                [DELIVERY_ADDRESS_DADATA]:
                    deliveryAddressMetaState.formState[DELIVERY_ADDRESS_DADATA],
                [DELIVERY_DATE]: deliveryBookCourierDeliveryState.formState[DELIVERY_DATE],
                [DELIVERY_DATE_SLOT_TIME]:
                    deliveryBookCourierDeliveryState.formState[DELIVERY_DATE_SLOT_TIME],
                [DELIVERY_DATE_SLOT]:
                    deliveryBookCourierDeliveryState.formState[DELIVERY_DATE_SLOT],
                [COMMENT]: deliveryBookCourierDeliveryState.formState[COMMENT],
            }}
            onSubmit={handleOnSubmit}
            view="without-shadow"
        >
            <AnalyticsSpy analytics={analytics} />
            <Text styler={{ marginTop: 0, marginBottom: 24 }} tag="h2" view="medium">
                {title}
            </Text>
            {showProgressBar && (
                <FormProgressBarV2Desktop
                    caption={progressBarSettings.caption}
                    currentStepName={progressBarSettings.currentStepName}
                    hatchValue={progressBarSettings.hatchValue}
                    header={progressBarSettings.header}
                    line={progressBarSettings.line}
                    maxStep={progressBarSettings.maxStep}
                    nextStepName={progressBarSettings.nextStepName}
                    steps={progressBarSettings.steps}
                    styler={{ marginBottom: 24 }}
                    value={progressBarSettings.value}
                />
            )}
            <Text styler={{ marginTop: 0, marginBottom: 24 }} tag="p" view="primary-medium">
                {subtitle}
            </Text>
            <Block notPadded styler={{ marginBottom: 24 }}>
                <Text styler={{ marginTop: 0, marginBottom: 24 }} tag="p" view="small">
                    {deliveryAddressLabel}
                </Text>
                <InvestAddressRegistrationField
                    disabled={isLoading}
                    isRequired
                    name={DELIVERY_ADDRESS}
                    onSelect={handleOnSelect}
                    settings={DEFAULT_DELIVERY_ADDRESS_SETTINGS}
                    validateType="deliveryAddress"
                />
                <AddressSpinner isLoading={deliveryAddressMetaState.isLoading} Text={Text} />
                <AddressValidationModalsDesktop />
            </Block>
            <Block notPadded styler={{ marginBottom: 24 }}>
                <Text styler={{ marginTop: 0, marginBottom: 24 }} tag="p" view="small">
                    {deliverySlotsLabel}
                </Text>
                <DeliverySlotsDesktop
                    analytics={{
                        onSelectTime: analytics.timeSlotClick,
                        onSelectDate: analytics.dateSlotClick,
                    }}
                    availableDates={deliveryBookCourierDeliveryState.availableDates}
                    disabled={isLoading}
                />
            </Block>
            <Block
                notPadded
                styler={{
                    marginBottom: 24,
                }}
            >
                <FormInput
                    disabled={isLoading}
                    name={COMMENT}
                    settings={DEFAULT_COMMENT_SETTINGS}
                />
            </Block>
            <Button loading={isLoading} size="s" type="submit" view="primary">
                {primaryButtonText}
            </Button>
        </Form>
    );
};
